import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import api from "../../../_core/api";
import { warning, handleErrors } from "../../../_core/services/toast";
import {
  Button,
  Form,
  Option,
  SingleSelect,
  Table,
} from "../../../_core/_components";
import { FullScreenLoading } from "../../../_core/_components/fullscreen-loading";
import { DateTimeInput } from "../../../_core/_components/date-time-input";
import { requests } from "../../../requests";
import { useCache } from "../../../_core/cache";

export type BillingItem = {
  billingGroup: string;
  portifolio: string;
  dueDate: string | null;
  overdueThisMonth: number;
  overdue120Days: number;
  totalOverdue: number;
  amountDue: number;
  priority: number;
  documentNumber: string;
  status: string;
  updateDate: string | null;
};

export const PriorizacaoGrupoCobranca: React.FC = () => {
  const history = useHistory();

  const { response: responsePortfolios, isLoading: isLoadingPortfolios } =
    useCache({
      key: "getPortfolios",
      fn: requests.get("getPortfolios"),
    });

  const {
    response: responseGruposCobranca,
    isLoading: isLoadingGruposCobranca,
  } = useCache({
    key: "getGruposCobranca",
    fn: requests.get("getGruposCobranca"),
  });

  const [loading, setLoading] = useState(false);
  const [portifolioList, setPortifolioList] = useState<Option[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Option>();
  const [selectedBillingGroup, setSelectedBillingGroup] = useState<Option>();
  const [billingGroupsList, setBillingGroupsList] = useState<Option[]>([]);
  const [monthYear, setMonthYear] = useState(moment().format("YYYY-MM"));
  const [data, setData] = useState<BillingItem[]>();

  useEffect(() => {
    if (responsePortfolios !== undefined) {
      const listPortfolios: Option[] = [];
      responsePortfolios.data.content.map((item: string) => {
        listPortfolios.push({
          value: item,
          label: item,
        });
        return null;
      });
      setPortifolioList(listPortfolios);
    }
  }, [responsePortfolios]);

  useEffect(() => {
    if (responseGruposCobranca !== undefined) {
      const listGrupoCobranca: Option[] = [];
      responseGruposCobranca.data.content.forEach((item: any) => {
        listGrupoCobranca.push({
          value: item.idGrupoCobranca,
          label: item.nome,
        });
      });

      setBillingGroupsList(listGrupoCobranca);
    }
  }, [responseGruposCobranca]);

  const isDisabled = useMemo(() => {
    if (monthYear.length !== 7) return true;
    if (!selectedPortfolio) return true;
    return false;
  }, [monthYear, selectedPortfolio]);

  const submit = async () => {
    setLoading(true);
    try {
      const [year, month] = monthYear.split("-");
      const { data } = await api.post(
        "api/allos/v1/prioritization/past-due-balance/billing-group",
        {
          month,
          year,
          portifolio: [selectedPortfolio!.value],
          grupoCobranca: selectedBillingGroup
            ? [selectedBillingGroup.label]
            : undefined,
        }
      );
      if (_.isEmpty(data.data)) {
        warning("A busca não retornou resultados");
      } else {
        setData(data.data);
      }
    } catch (error) {
      handleErrors(error);
    }
    setLoading(false);
  };

  const handleClick = (item: BillingItem) => {
    const params = new URLSearchParams();
    const [year, month] = monthYear.split("-");

    params.append("portifolio", item.portifolio);
    params.append("grupo-cobranca", item.billingGroup);

    params.append("month", month);
    params.append("year", year);

    history.push(`/boletos-grupo-cobranca?${params.toString()}`);
  };

  const renderedData = useMemo(() => {
    if (data && data.length > 0) {
      return data.map((item) => ({
        ...item,
        overdueThisMonth: item.overdueThisMonth.toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        }),
        overdue120Days: item.overdue120Days.toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        }),
        totalOverdue: item.totalOverdue.toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        }),
        amountDue: item.amountDue.toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        }),
        updateDate: item.updateDate
          ? moment(item.updateDate).format("DD/MM/YYYY")
          : "-",
      }));
    }
    return [];
  }, [data]);

  return (
    <>
      <FullScreenLoading
        isEnabled={loading || isLoadingPortfolios || isLoadingGruposCobranca}
      />
      <Form
        columns={3}
        items={[
          <SingleSelect
            placeholder="Portfólios"
            state={[selectedPortfolio, setSelectedPortfolio]}
            options={portifolioList}
            key={0}
          />,
          <SingleSelect
            placeholder="Grupo de cobrança"
            state={[selectedBillingGroup, setSelectedBillingGroup]}
            options={billingGroupsList}
            key={1}
          />,
          <DateTimeInput
            placeholder="MM/AAAA"
            label="Mês/ano de início"
            state={[monthYear, setMonthYear]}
            key={2}
          />,
        ]}
        submitButton={
          <Button onClick={submit} disabled={isDisabled} text="Pesquisar" />
        }
      />

      {data && data.length > 0 && (
        <Table
          columns={[
            { key: "billingGroup", label: "Grupo", orderable: true },
            { key: "portifolio", label: "Portfólio", orderable: true },
            { key: "overdueThisMonth", label: "Mês Atual", orderable: true },
            { key: "overdue120Days", label: "120+ Dias", orderable: true },
            { key: "totalOverdue", label: "Total Vencido", orderable: true },
            { key: "amountDue", label: "À Vencer", orderable: true },
            { key: "status", label: "Status", orderable: true },
            { key: "updateDate", label: "Data Contato", orderable: true },
          ]}
          data={renderedData}
          onRowClick={handleClick}
        />
      )}
    </>
  );
};
